import {
  createKeyFrameRequestManager,
  KEY_FRAME_REQUEST_METHOD
} from './keyFrameRequestManager'

export { KEY_FRAME_REQUEST_METHOD }

const KEY_FRAME_REQUEST_INTERVAL_MS = 10000
// 首個遠端 RTP 抵達後的 IDR 送出時點，相對於 unmute 計算。
// rtpengine 對本方向的端點學習未必與遠端首包同時完成，單一 IDR 仍可能被丟棄，
// 因此以遞增間隔連送三次提高命中機率。這三次刻意豁免下方的間隔節流。
const FIRST_REMOTE_RTP_REQUEST_OFFSETS_MS = Object.freeze([500, 1500, 4000])

// 回報用的回呼只是觀測管道，拋錯不得影響通話或排程，因此一律吞下例外。
// 若不吞：成功回呼拋錯會被外層 catch 誤判成請求失敗；失敗回呼拋錯則因 requestKeyFrame
// 由 setTimeout 裸呼叫（無 .catch）而變成 unhandledrejection。
const notifySafely = (callback, payload) => {
  if (typeof callback !== 'function') return

  try {
    callback(payload)
  } catch (error) {
    // 此處已是最外層的回報邊界，沒有更外層的管道可轉報，只能中止於此。
  }
}

const createKeyFrameRequestScheduler = ({
  request,
  onRequestSucceeded,
  onRequestFailed
}) => {
  // state 物件身分同時作為 generation token；stop() 或下一通 start() 後，
  // 舊 timer／Promise callback 即使稍後完成，也無法再請求新的通話。
  let activeState = null
  // SIP early media 可能使 unmute 早於 confirmed；以 PeerConnection 身分保存通知，
  // 讓 start() 啟動排程器後補回同一通話的首包序列。WeakSet 不會延長已結束通話的生命週期。
  let pendingFirstRemoteRtpPeerConnections = new WeakSet()

  const isActive = (state) => activeState === state

  const schedule = (state, delayMs, trigger) => {
    if (!isActive(state)) return

    if (state.timer !== null) {
      clearTimeout(state.timer)
    }

    // 使用單次 timer 並從實際請求時間重新排程；timer 延遲時不補發漏掉的週期。
    // 首包序列與定期週期共用同一個 timer：序列進行中 timer 恆持有下一個序列時點，
    // 定期請求無法插入其間，因此不需要兩個獨立 timer。
    state.timer = setTimeout(() => {
      state.timer = null
      requestKeyFrame(state, trigger)
    }, Math.max(0, delayMs))
  }

  // 決定本次請求結束後的下一個時點：首包序列尚有剩餘時點時優先取用，否則回到定期週期。
  const scheduleNextRequest = (state) => {
    const nextOffsetMs = state.pendingFirstRtpOffsetsMs.shift()

    if (nextOffsetMs !== undefined) {
      // 以 unmute 時點為固定基準，序列不會因前一次請求耗時而整體往後累積。
      schedule(
        state,
        state.firstRemoteRtpAt + nextOffsetMs - performance.now(),
        'first-remote-rtp'
      )
      return
    }

    schedule(
      state,
      state.lastAttemptAt + KEY_FRAME_REQUEST_INTERVAL_MS - performance.now(),
      'periodic'
    )
  }

  const requestKeyFrame = async (state, trigger) => {
    if (!isActive(state) || state.requesting) return

    const now = performance.now()

    // 節流只作用於定期請求；首包序列的三次請求屬於同一個恢復動作，刻意不受此限制。
    if (trigger === 'periodic' && state.lastAttemptAt !== null) {
      const remainingMs = KEY_FRAME_REQUEST_INTERVAL_MS - (now - state.lastAttemptAt)

      if (remainingMs > 0) {
        schedule(state, remainingMs, 'periodic')
        return
      }
    }

    // 必須在第一個 await 前鎖定，否則首包與 timer 同時觸發時會送出兩次請求。
    state.requesting = true
    // 瀏覽器能力選路已由 start() 於通話開始時預先觸發（見下方 createKeyFrameRecovery.start），
    // 多數情況會在遠端首包抵達前完成。但 SIP 183 early media 使 unmute 早於 confirmed 時，
    // 預熱時間趨近於零，第一次請求仍可能等待選路（上限為能力探測的 5 秒逾時）。
    // 時點一律以進入請求流程時起算，因此選路較慢時，首包序列後續時點會被壓縮至該次請求
    // 結束後連續送出；選路結果快取後恢復正常。
    state.lastAttemptAt = now

    try {
      const result = await request(state.videoSender)

      if (isActive(state)) {
        notifySafely(onRequestSucceeded, { result, trigger })
      }
    } catch (error) {
      if (isActive(state)) {
        notifySafely(onRequestFailed, { error, trigger })
      }
    } finally {
      if (isActive(state)) {
        state.requesting = false
        scheduleNextRequest(state)
      }
    }
  }

  const stop = (peerConnection = null, preservePendingFirstRemoteRtp = false) => {
    const state = activeState

    if (!preservePendingFirstRemoteRtp) {
      if (peerConnection) {
        pendingFirstRemoteRtpPeerConnections.delete(peerConnection)
      } else {
        pendingFirstRemoteRtpPeerConnections = new WeakSet()
      }
    }

    // 指定 PeerConnection 時，只能停止所屬通話，避免舊 ended／failed 事件清掉新通話。
    if (!state || (peerConnection && state.peerConnection !== peerConnection)) {
      return null
    }

    if (state.timer !== null) {
      clearTimeout(state.timer)
    }

    activeState = null
    return state.peerConnection
  }

  const start = (peerConnection, videoSender) => {
    const firstRemoteVideoRtpReceived = pendingFirstRemoteRtpPeerConnections.delete(peerConnection)
    stop()

    const state = {
      peerConnection,
      videoSender,
      timer: null,
      firstRemoteVideoRtpReceived: false,
      firstRemoteRtpAt: null,
      pendingFirstRtpOffsetsMs: [],
      lastAttemptAt: null,
      requesting: false
    }

    activeState = state
    // confirmed 定義為第 0 秒；此處只排定第 10 秒的首次週期，不立即請求。
    schedule(state, KEY_FRAME_REQUEST_INTERVAL_MS, 'periodic')

    if (firstRemoteVideoRtpReceived) {
      notifyRemoteVideoRtp(peerConnection)
    }
  }

  const notifyRemoteVideoRtp = (peerConnection) => {
    // 本函式由 JsSIP 事件處理器呼叫，且 WeakSet.add(null) 會拋 TypeError
    // （不同於 delete 與 WeakMap.get 的靜默回傳），因此在最前面擋掉空值，
    // 維持本模組對外 API 一致的 null-safe 行為。
    if (!peerConnection) return

    const state = activeState

    if (!state) {
      pendingFirstRemoteRtpPeerConnections.add(peerConnection)
      return
    }

    if (state.peerConnection !== peerConnection || state.firstRemoteVideoRtpReceived) return

    state.firstRemoteVideoRtpReceived = true
    state.firstRemoteRtpAt = performance.now()
    state.pendingFirstRtpOffsetsMs = [...FIRST_REMOTE_RTP_REQUEST_OFFSETS_MS]

    // 定期請求正在執行時不搶排；其 finally 會接手序列，避免第一個時點被覆寫而漏送。
    if (!state.requesting) {
      scheduleNextRequest(state)
    }
  }

  return {
    start,
    notifyRemoteVideoRtp,
    stop
  }
}

/**
 * 隱藏 10 秒排程、瀏覽器選路與通話資源清理。
 * 呼叫端只需提供 confirmed 與遠端視訊 unmute 兩個既有事件時機，兩者順序不受限制。
 *
 * @param {object}   options
 * @param {Function} options.onRequestSucceeded ({ result, trigger }) => void
 * @param {Function} options.onRequestFailed    ({ error, trigger }) => void
 * @param {Function} [options.onFallback]       ({ selectionReason, error }) => void，只在異常回退時觸發
 * @param {Function} [options.onWorkerFailure]  ({ reason, error }) => void，已選定的 Worker 路徑中途失效時觸發
 */
export const createKeyFrameRecovery = ({
  onRequestSucceeded,
  onRequestFailed,
  onFallback,
  onWorkerFailure
}) => {
  const manager = createKeyFrameRequestManager({ onFallback, onWorkerFailure })
  const scheduler = createKeyFrameRequestScheduler({
    request: (videoSender) => manager.request(videoSender),
    onRequestSucceeded,
    onRequestFailed
  })

  const stop = (peerConnection = null) => {
    // scheduler 只在 peerConnection 屬於目前排程時回傳它。不屬於時（舊通話的
    // ended／failed 較晚抵達）仍須釋放該通話遺留的 Worker，因此以傳入值遞補。
    const stoppedPeerConnection = scheduler.stop(peerConnection)
    manager.disposePeerConnection(stoppedPeerConnection || peerConnection)
  }

  const start = (peerConnection, videoSender) => {
    // 保留即將啟動之通話的 early-media 通知；scheduler.start() 取出後會清除其餘 pending 狀態。
    const stoppedPeerConnection = scheduler.stop(null, true)
    manager.disposePeerConnection(stoppedPeerConnection)
    // 必須在 PeerConnection 仍開啟時記錄配對，供 ended／failed 之後查表釋放 Worker
    // （見 keyFrameRequestManager.js 的 peerConnectionVideoSenders 說明）；
    // 先釋放前一通話再註冊新通話，避免順序反過來時誤刪剛寫入的配對。
    manager.registerCall(peerConnection, videoSender)
    // 預熱選路：能力探測最長需要數秒，若留到第一次 IDR 請求（unmute + 0.5 秒）才做，
    // 探測就會壓在通話建立最忙碌的時刻，逾時後整通通話會被永久釘在 set-parameters。
    // 提前於此觸發，讓探測與振鈴→接通→unmute 的等待時間重疊。
    // 刻意不 await、不接 .then：回傳的是快取後的選路 Promise，由建構方式保證不會 reject
    // （selectKeyFrameRequestPath 的失敗已被 preparePath 內的 .catch 收斂成 path-selection-failed），
    // 且稍後的 request() 會等待同一個 Promise，因此這裡只取其副作用，結果一律忽略。
    manager.preparePath(videoSender)
    scheduler.start(peerConnection, videoSender)
  }

  const dispose = () => {
    scheduler.stop()
    manager.disposeAll()
  }

  return {
    start,
    notifyRemoteVideoRtp: scheduler.notifyRemoteVideoRtp,
    stop,
    dispose
  }
}
